import React, { useContext } from 'react'
import { useHistory, Redirect } from 'react-router-dom'
import { useFormik } from 'formik'

import GoogleLogin from './GoogleLogin/'
import { AuthContext } from 'Context/Auth/'
import { loginUser } from 'Util/Server/Auth'
import { User, UserAdd } from 'Svg/'
import Button, { ButtonType, ButtonRole } from 'Common/Button/'

interface FormErrors {
  email?: string
  password?: string
}

interface LoginProps {
  afterLoginCallback: () => void
}

export function LoginForm({ afterLoginCallback }: LoginProps) {
  const history = useHistory()
  const { updateAuthStatus } = useContext(AuthContext)

  const form = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validate: values => {
      const errors: FormErrors = {}

      // email validation
      if (!values.email) {
        errors.email = 'Please enter email ...'
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
      ) {
        errors.email = 'Invalid email address'
      }

      // password validation
      if (!values.password) {
        errors.password = 'Please enter a password (min 7 characters)'
      } else if (values.password.length < 7) {
        errors.password = 'Password should be atleast 7 characters'
      }

      return errors
    },
    onSubmit: values => {
      // take necessary values
      const { email, password } = values

      loginUser({
        email,
        password,
        setAuthStatus: updateAuthStatus,
        successCallback: () => {
          afterLoginCallback()
        },
        errorCallback: () => {},
      })
    },
  })

  const isEmailValid: boolean = !(form.touched.email && form.errors.email)
  const isPasswordValid: boolean = !(
    form.touched.password && form.errors.password
  )

  return (
    <div className="w-full">
      <div className="max-w-xl mx-auto bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        {/* Google Login */}
        <GoogleLogin />

        {/* Normal Login form */}
        <form
          className="my-5 mt-8 border-t border-gray-200 pt-5"
          onSubmit={form.handleSubmit}
        >
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="email"
            >
              Email
            </label>
            <input
              className={isEmailValid ? 'form-ok' : 'form-notok'}
              id="email"
              name={'email'}
              type="email"
              placeholder="Email ..."
              value={form.values.email}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
            {!isEmailValid && (
              <p className="text-red-500 text-xs italic">{form.errors.email}</p>
            )}
          </div>
          <div className="mb-6">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="password"
            >
              Password
            </label>
            <input
              className={isPasswordValid ? 'form-ok' : 'form-notok'}
              id="password"
              name={'password'}
              type="password"
              placeholder="******************"
              value={form.values.password}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
            />
            {!isPasswordValid && (
              <p className="text-red-500 text-xs italic">
                {form.errors.password}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <Button
              role={ButtonRole.Submit}
              type={ButtonType.Primary}
              text={'Login'}
              Icon={User}
            />
            <Button
              type={ButtonType.Outline}
              text={'Forgot Password?'}
              onClick={() => history.push('/forgot-password')}
            />
          </div>
          <div className="mt-5 mb-1 py-3 border-t">
            <p className="text-center text-gray-500 text-xs">
              Don't have an account?
            </p>
          </div>
          <div className="flex items-center justify-center">
            <Button
              type={ButtonType.Primary}
              onClick={() => history.push('/create-account')}
              text={'Create Account'}
              Icon={UserAdd}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

function Login() {
  const history = useHistory()
  const { authStatus } = useContext(AuthContext)
  const isLoggedIn = authStatus && authStatus.user && authStatus.token

  return isLoggedIn ? (
    <Redirect to={'/'} />
  ) : (
    <LoginForm
      afterLoginCallback={() => {
        history.push('/')
      }}
    />
  )
}

export default Login
